import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  getRequests,
  requestsSelectors,
  setSearchParameter,
  setSortParameter,
  setIsFetchingNext,
} from 'state/normalized/requests/requestsSlice';
import { setSelectAll } from 'state/ExportRequests/ExportRequestsSlice';
import { useAppThunkDispatch } from 'state/store';
import { PersistentFilterTypeKeys, RangeFilter } from 'utils/types/filters';
import { chunkArray } from 'Pages/helpers';
import { Request } from 'types/store/normalized';
import { RequestStatus } from 'utils/types/request';
import { getUserSummaries } from 'state/normalized/userSummaries/userSummariesSlice';
import { getFormSummaries } from 'state/normalized/formSummaries/formSummariesSlice';
import { getProjectSummaries } from 'state/normalized/projectSummaries/projectSummariesSlice';
import { getProjectPermissions } from 'state/normalized/projectPermissions/projectPermissionsSlice';
import { getBusinessUnitSummaries } from 'state/normalized/businessUnitSummaries/businessUnitSummariesSlice';
import { fetchFilterSettingByType } from 'state/Settings/Filters/FiltersSlice';
import { SLICE_STATUS, SORT_ORDER } from 'utils/constants';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/ui';
import { getRequestPermissions } from 'state/normalized/requestPermissions/requestPermissionsSlice';

interface UseRequestsTableParameters {
  filterType: PersistentFilterTypeKeys;
  statuses?: readonly RequestStatus[];
  filterAppliedRequesterIds?: string[];
  projectIds?: string[];
  filterAppliedOwnerIds?: string[];
  filterAppliedBusinessUnitIds?: string[];
  numberOfProjects?: string[];
  filterAppliedPriority?: string[];
  formIds?: string[];
  updatedAt: RangeFilter;
  createdAt: RangeFilter;
  submittedAt: RangeFilter;
  decisionDate: RangeFilter;
  myRequests?: boolean;
  shouldFetchData?: boolean;
  archived?: boolean;
}

interface UseRequestsTableResponse {
  requests: Request[];
  status: string;
  canFetchMore: boolean;
  onFetchMore: () => void;
  searchRequests: (search: string) => void;
  isSearchApplied: boolean;
  /** Request id of the last successfully patched request; useful to know patch was triggered and completed */
  lastPatchedRequestId: string | null;
}
const useRequestsTable = ({
  filterType,
  statuses,
  filterAppliedRequesterIds,
  projectIds,
  filterAppliedOwnerIds,
  filterAppliedBusinessUnitIds,
  numberOfProjects,
  filterAppliedPriority,
  formIds,
  updatedAt,
  createdAt,
  submittedAt,
  decisionDate,
  myRequests,
  shouldFetchData = true,
  archived,
}: UseRequestsTableParameters): UseRequestsTableResponse => {
  const dispatch = useAppThunkDispatch();
  const requests = useSelector(requestsSelectors.selectAll);
  const status = useSelector(requestsSelectors.selectStatus);
  const lastPatchedRequestId = useSelector(
    requestsSelectors.selectLastPatchedRequestId
  );
  const canFetchMore = useSelector(requestsSelectors.selectCanFetchMore);
  const searchParameter = useSelector(requestsSelectors.selectSearchParameter);
  const [fetchedFilterSettings, setFetchedFilterSettings] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      await dispatch(fetchFilterSettingByType(filterType));
      if (!cancelled) setFetchedFilterSettings(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, filterType]);

  useEffect(() => {
    // CGP-268: when the applied filters produce no fetchable statuses,
    // statuses is []. The getRequests thunk short-circuits an empty status
    // list into an empty page, so skipping the dispatch here is only an
    // optimization: this hook already masks the results at render, so the
    // no-op fetch cycle would be wasted work.
    fetchedFilterSettings &&
      shouldFetchData &&
      dispatch(
        getRequests({
          status: statuses,
          filterParams: {
            requesterIds: filterAppliedRequesterIds,
            projectIds,
            ownerIds: filterAppliedOwnerIds,
            businessUnitIds: filterAppliedBusinessUnitIds,
            numberOfProjects,
            ldPriority: filterAppliedPriority,
            formIds,
            updatedAt,
            createdAt,
            submittedAt,
            decisionDate,
            myRequests,
            archived,
          },
        })
      );

    return () => {
      dispatch(setSearchParameter(''));
      dispatch(setSelectAll(false));
      dispatch(setSortParameter({ sortBy: '', sortOrder: SORT_ORDER.ASC }));
    };
  }, [
    dispatch,
    statuses,
    filterAppliedBusinessUnitIds,
    filterAppliedRequesterIds,
    fetchedFilterSettings,
    projectIds,
    filterAppliedOwnerIds,
    numberOfProjects,
    filterAppliedPriority,
    formIds,
    updatedAt,
    createdAt,
    submittedAt,
    decisionDate,
    myRequests,
    archived,
    shouldFetchData,
  ]);

  const isSearchApplied = searchParameter !== '';

  const searchRequests = (search: string) => {
    if (search === searchParameter) return;
    dispatch(setSearchParameter(search));
    dispatch(
      getRequests({
        status: statuses,
        filterParams: {
          requesterIds: filterAppliedRequesterIds,
          projectIds,
          ownerIds: filterAppliedOwnerIds,
          businessUnitIds: filterAppliedBusinessUnitIds,
          numberOfProjects,
          ldPriority: filterAppliedPriority,
          formIds,
          updatedAt,
          createdAt,
          submittedAt,
          decisionDate,
          myRequests,
          archived,
        },
      })
    );
  };

  /**
   * Requests already hydrated, keyed on the entity OBJECT rather than the id:
   * the adapter preserves the reference of untouched rows (so appending a page
   * skips earlier rows) while a patched row becomes a new object and re-hydrates.
   */
  const hydratedRequestsRef = useRef<WeakSet<Request>>(new WeakSet());

  /**
   * Fetches the users, projects, forms and business units the rows reference.
   *
   * INVARIANT: derive the ids from `newRequests`, never from `requests`.
   * Scanning the whole accumulated list re-chunks it by page size on every
   * change, so scrolling to page k dispatches k chunk thunks. Those thunks
   * dedupe against their caches only INSIDE the payload creator, so each no-op
   * chunk still dispatches pending/fulfilled and still notifies every store
   * subscriber — which made "load more" cost O(pages^2).
   */
  useEffect(() => {
    const newRequests = requests.filter(
      (request) => !hydratedRequestsRef.current.has(request)
    );
    if (newRequests.length === 0) return;

    newRequests.forEach((request) => hydratedRequestsRef.current.add(request));

    const ownerIds = newRequests.map((request) => request.ownerIds).flat();
    const requesterIds: string[] = newRequests
      .map((request) => request.requesterId)
      .filter((s): s is string => s !== undefined);
    const formIds = newRequests.map((request) => request.formId);
    const projectIds = newRequests.map((request) => request.projectIds).flat();
    const businessUnitIds: string[] = newRequests.map(
      (request) => request.businessTeamId
    ) as string[];
    const requestIds = newRequests.map((request) => request.id);

    if (requestIds.length > 0) {
      const uniqueRequestIds = [...new Set(requestIds)];
      const chunks = chunkArray(uniqueRequestIds, DEFAULT_PAGE_SIZE);

      chunks.forEach((chunk) => {
        dispatch(
          getRequestPermissions({
            requestIds: chunk,
          })
        );
      });
    }

    if (requesterIds.length > 0 || ownerIds.length > 0) {
      const uniqueUserIds = [...new Set([...ownerIds, ...requesterIds])];
      const chunks = chunkArray(uniqueUserIds, DEFAULT_PAGE_SIZE);

      chunks.forEach((chunk) => {
        dispatch(getUserSummaries({ userIds: chunk }));
      });
    }

    if (projectIds.length > 0) {
      dispatch(getProjectSummaries({ projectIds }));
      dispatch(getProjectPermissions({ projectIds }));
    }

    if (formIds.length > 0) {
      dispatch(getFormSummaries({ formIds }));
    }

    if (businessUnitIds.length > 0) {
      dispatch(getBusinessUnitSummaries({ businessUnitIds: businessUnitIds }));
    }
  }, [requests, dispatch]);

  const onFetchMore = async () => {
    dispatch(setIsFetchingNext(true));
    await dispatch(
      getRequests({
        status: statuses,
        filterParams: {
          requesterIds: filterAppliedRequesterIds,
          projectIds,
          ownerIds: filterAppliedOwnerIds,
          businessUnitIds: filterAppliedBusinessUnitIds,
          numberOfProjects,
          ldPriority: filterAppliedPriority,
          formIds,
          updatedAt,
          createdAt,
          submittedAt,
          decisionDate,
          myRequests,
          archived,
        },
        fetchNext: true,
      })
    );
    dispatch(setIsFetchingNext(false));
  };

  if (!shouldFetchData) {
    return {
      requests: [],
      status: SLICE_STATUS.SUCCESS,
      canFetchMore: false,
      onFetchMore: () => {},
      searchRequests: () => {},
      isSearchApplied: false,
      lastPatchedRequestId: null,
    };
  }
  return {
    requests,
    status,
    canFetchMore,
    onFetchMore,
    searchRequests,
    isSearchApplied,
    lastPatchedRequestId,
  };
};

export default useRequestsTable;
